import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'
import { Grid } from '@mui/material'

import { GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid'

import {
    defaultMemberPermission,
    memberPermissionSchema,
} from '@/api/mem/memberPermission'
import {
    memberPermissionsQueryOptions,
    useCloneMemberPermissionMutation,
    useCreateMemberPermissionApiKeyMutation,
    useCreateMemberPermissionMutation,
    useDeleteMemberPermissionApiKeyMutation,
    useDeleteMemberPermissionMutation,
    useUpdateMemberPermissionMutation,
} from '@/api/mem/memberPermissionQueryOptions'
import { defaultMemberSection } from '@/api/mem/memberSection'
import { MemberPermissionType } from '@/api/memberPermissions'
import { OrganizationLevelType } from '@/api/organizations'
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions'
import CreateDetailAccordion, {
    useCreateDetail,
} from '@/components/detail/CreateDetailRecord'
import EditGrid, { useEditGrid } from '@/components/detail/EditGrid'
import useBasicGridColumns from '@/components/grid/BasicGridColumns'
import useDetailCrudEvents from '@/components/hooks/useDetailCrudEvents'
import useResponsive from '@/components/hooks/useResponsive'
import UserPanel from '@/components/member/UserPanel'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import SelectOrganization from '@/components/select/SelectOrganization'
import SelectRole from '@/components/select/SelectRole'
import { getOrganization } from '@/store/auth/authSlice'
import { yupResolver } from '@hookform/resolvers/yup'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import KeyIcon from '@mui/icons-material/Key'
import KeyOffIcon from '@mui/icons-material/KeyOff'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Tooltip from '@mui/material/Tooltip'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'
import React from 'react'
import { useSelector } from 'react-redux'

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/permissions',
)({
    component: MemberComponent,
})

function MemberComponent() {
    const params = Route.useParams()
    const memberPermissionsQuery = useSuspenseQuery(
        memberPermissionsQueryOptions(params.memberUuid),
    )
    const memberPermissions = memberPermissionsQuery.data

    const [openApiKey, setOpenApiKey] = React.useState(false);
    const [apiKey, setApiKey] = React.useState('');

    const sessionOrganization = useSelector(getOrganization)

    const { onCreated, onUpdated, onDeleted, onError } =
        useDetailCrudEvents<MemberPermissionType>('memberPermission')

    const createMemberPermissionMutation = useCreateMemberPermissionMutation(
        params.memberUuid,
        onCreated,
        onError,
    )
    const updateMemberPermissionMutation = useUpdateMemberPermissionMutation(
        params.memberUuid,
        onUpdated,
        onError,
    )
    const deleteMemberPermissionMutation = useDeleteMemberPermissionMutation(
        params.memberUuid,
        onDeleted,
        onError,
    )

    const cloneMemberPermissionMutation = useCloneMemberPermissionMutation(
        params.memberUuid,
        onCreated,
        onError,
    )

    const onApiKeyCreated = (data: any) => {
        setApiKey(data)
        setOpenApiKey(true)
    }

    const createMemberPermissionApiKeyMutation = useCreateMemberPermissionApiKeyMutation(
        params.memberUuid,
        onApiKeyCreated,
        onError,
    )

    const deleteMemberPermissionApiKeyMutation = useDeleteMemberPermissionApiKeyMutation(
        params.memberUuid,
        () => {
            enqueueSnackbar(t('action.deleted', { entity: t('entity.apiKey') }), { variant: 'success' });
        },
        onError,
    )

    const basicGridColumns = useBasicGridColumns()

    const onSubmit = (data: MemberPermissionType) => {
        createMemberPermissionMutation.mutate({ ...data, ...{ uuid: '' } })
    }

    const handleCloneClick = (_id: GridRowId, row: any) => () => {
        cloneMemberPermissionMutation.mutate({ ...row })
    }

    const handleApiKeyClick = (_id: GridRowId, row: any) => () => {
        createMemberPermissionApiKeyMutation.mutate({ ...row })
    }

    const deleteApiKey = (_id: GridRowId, row: any) => () => {
        deleteMemberPermissionApiKeyMutation.mutate({ ...row })
    }

    const handleClose = () => {
        setApiKey('');
        setOpenApiKey(false);
    };

    const permissionsQuery = useSuspenseQuery(fetchDetailPermissionsQueryOptions('member', params.memberUuid, ['MemberPermissionClone', 'MemberPermissionApiKey', 'MemberPermission']))
    const permissions = permissionsQuery.data
    const clonePermission = permissions && permissions.filter(p => p.detail === 'MemberPermissionClone' && p.permissions.some(a => a === 'create')).length > 0
    const createApiKeyPermission = permissions && permissions.filter(p => p.detail === 'MemberPermissionApiKey' && p.permissions.some(a => a === 'create')).length > 0
    const deleteApiKeyPermission = permissions && permissions.filter(p => p.detail === 'MemberPermissionApiKey' && p.permissions.some(a => a === 'delete')).length > 0

    const { xsmall, small, medium, large, xlarge } = useResponsive()

    const columns: GridColDef[] = []
    columns.push({
        field: 'permission',
        headerName: t('entity.permission'),
        minWidth: 100,
        valueGetter: (_value, row) => `${row.role.name || ''}`,

    })
    columns.push(basicGridColumns.organizationNameColumn)

    if (xsmall || small) {
        columns.push(basicGridColumns.validPeriodColumn)
    }
    if (medium || large || xlarge) {
        columns.push(
            basicGridColumns.descriptionColumn,
        )
        columns.push(
            basicGridColumns.startDateColumn,
            basicGridColumns.endDateColumn,
        )
        columns.push(basicGridColumns.activeColumn)
        columns.push(
            {
                field: 'apiKey',
                headerName: 'API Key',
                renderCell: (params) => {
                    return params.row.apiKey ? <Tooltip title={t('property.memberPermission.apiKeyExists')}><KeyIcon /></Tooltip> : <></>
                }
            }
        )

        if (createApiKeyPermission) {
            columns.push(
                {
                    field: 'apiKeyAction',
                    type: 'actions',
                    headerName: 'API Key verwalten',
                    width: 50,
                    getActions: ({ id, row }: { id: GridRowId, row: any }) => {
                        if (row.apiKey) {
                            return [<GridActionsCellItem
                                icon={<Tooltip title={t('action.deleteApiKey')}><KeyOffIcon /></Tooltip>}
                                label={t('action.deleteApiKey')}
                                key={`deleteApiKey-${id}`}
                                disabled={!deleteApiKeyPermission}
                                onClick={deleteApiKey(id, row)}
                            />]
                        } else {
                            return [
                                <GridActionsCellItem
                                    icon={<Tooltip title={t('action.createApiKey')}><KeyIcon /></Tooltip>}
                                    label={t('action.createApiKey')}
                                    key={`createApiKey-${id}`}
                                    disabled={!createApiKeyPermission}
                                    onClick={handleApiKeyClick(id, row)}
                                />,
                            ]
                        }
                    },
                },
            )
        }
        if (clonePermission) {
            columns.push(
                {
                    field: 'cloneAction',
                    type: 'actions',
                    headerName: '',
                    width: 50,
                    getActions: ({ id, row }: { id: GridRowId, row: any }) => {
                        return [
                            <GridActionsCellItem
                                icon={<FileCopyIcon />}
                                label='Clone'
                                key={`clone-${id}`}
                                onClick={handleCloneClick(id, row)}
                            />
                        ]
                    },
                }
            )
        }
    }

    const createDetail = useCreateDetail<MemberPermissionType>({
        onSubmit,
        resolver: yupResolver<MemberPermissionType, any, any>(memberPermissionSchema),
        defaultValues: {
            ...defaultMemberPermission,
            uuid: 'new',
            member: {
                uuid: params.memberUuid,
            },
            organization: {
                uuid: sessionOrganization.uuid,
            },
        },
        title: t('action.assign', { entity: t('entity.permission') }),
    })

    const editGrid = useEditGrid<MemberPermissionType>({
        defaultValues: defaultMemberPermission,
        updateMutation: updateMemberPermissionMutation,
        deleteMutation: deleteMemberPermissionMutation,
        records: memberPermissions,
        columns: columns,
        title: t('entity.permission'),
    })

    const createable =
        permissions &&
        permissions.filter(
            (p) =>
                p.detail === 'MemberPermission' && p.permissions.some((a) => a === 'create'),
        ).length > 0


    if (memberPermissionsQuery.isLoading || memberPermissionsQuery.isFetching) {
        return <LoadProgress />
    }

    return (<>
        <Box sx={{ paddingBottom: 2 }}>
            <UserPanel memberUuid={params.memberUuid} />
        </Box>
        {createable && (
            <CreateDetailAccordion {...createDetail}>
                <Grid container spacing={2}>
                    <input
                        type="hidden"
                        defaultValue={params.memberUuid}
                        {...createDetail.form.register('member.uuid')}
                    />
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <SelectOrganization
                            control={createDetail.form.control}
                            name="organization.uuid"
                            includeCurrentOrganization={true}
                            organizationLevels={[
                                OrganizationLevelType.TROOP,
                                OrganizationLevelType.KOLONNE,
                                OrganizationLevelType.DIVISION,
                                OrganizationLevelType.FEDERATION,
                            ]}
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <SelectRole control={createDetail.form.control} name="role.abbreviation" />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <ScoregDatePicker
                            name="startDate"
                            defaultValue={dayjs(defaultMemberSection.startDate)}
                            disabled={false}
                            control={createDetail.form.control}
                            label="von"
                        />
                    </Grid>
                </Grid>
            </CreateDetailAccordion>
        )}
        <EditGrid {...editGrid}></EditGrid>

        <Dialog
            open={openApiKey}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                {t('title.apiKeyCreated', { apiKey: apiKey })}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {t('message.apiKeyCreated', { apiKey: apiKey })}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>{t('action.close')}</Button>
            </DialogActions>
        </Dialog>
    </>);
}
